// Catalog page — read-only browse of catalogued content, in two views:
//
//   * Documents — each :Document root drilled down through its :contains
//     hierarchy of sections (to any depth). The view catalog FILES are built
//     for (citations, section increments).
//   * Items — the generic class → paged-instances browse, for catalog graphs
//     that hold products/models rather than documents. (This view predates the
//     document tree; dev.13 removed it, which left non-document catalogs
//     unrenderable — restored as a sibling view.)
//
// The page defaults to whichever view has content (documents win when both).
// The right panel renders the full entity view for the selected node in either
// view, reusing the Entity Info renderer so linked entities — and inbound
// @citation references — stay clickable.
//
// Selection is URL-driven (`?uri=…&graph=…`) so EntityView's internal
// navigation keeps working here and a bookmark or refresh restores the item.

import { useEffect, useMemo, useState } from 'react';
import { useDebounced } from '@/lib/useDebounced';
import { useNavigate, useSearchParams } from 'react-router';
import {
  useCatalogClasses,
  useCatalogDocuments,
  useCatalogInstances,
  type CatalogDocNode,
} from '@/lib/useCatalog';
import { CopyUriButton, EditableEntity } from '@/pages/EntityInfoPage';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';
import { canImportData, isQros, useMe } from '@/lib/useMe';
import { CatalogSourcesPanel } from '@/components/CatalogSourcesPanel';

type CatalogView = 'documents' | 'items' | 'sources';

// The Catalog is deliberately CROSS-GRAPH and carries no graph selector
// (GRAPH_SELECTION_SURFACES.md). The document tree and the Items class list are
// read from the catalog graph, which is where catalog files load; the instances
// behind a class are answered store-wide by the substrate, so an item held in
// another graph still appears. The detail pane likewise resolves the selected
// entity across all graphs (that scoping lives in EditableEntity), so "what
// cites it" spans the whole store.
const CATALOG_GRAPH = 'urn:tres:catalog';

export function CatalogPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectedUri = params.get('uri');

  const docs = useCatalogDocuments(CATALOG_GRAPH);
  // The Sources view manages what was loaded, so it is for those who can load.
  const me = useMe();
  const showSources = canImportData(me.data) && isQros(me.data);

  // View: user choice wins; before a choice, documents when any exist, else
  // items — so a models/products catalog renders instead of an empty tree.
  const [viewChoice, setViewChoice] = useState<CatalogView | null>(null);
  const view: CatalogView =
    viewChoice ?? (docs.data && docs.data.documents.length === 0 ? 'items' : 'documents');

  const handlePick = (uri: string) => {
    const next = new URLSearchParams();
    next.set('uri', uri);
    navigate({ search: next.toString() });
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">Catalog</h1>
          <p className="text-sm text-tres-text-secondary">
            {view === 'sources'
              ? 'Everything loaded into this site: each file, sheet and connector load, when it was loaded and by whom, and the mapping a sheet was read with.'
              : view === 'documents'
              ? 'Browse source documents and drill down through their sections. Select any document or section to see its detail — and what cites it.'
              : 'Browse the catalog by class. Select a class, then an item, to see everything the database knows about it. Items are listed from every graph, not just the catalog graph.'}
          </p>
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-border">
        {(
          [
            ['documents', 'Documents'],
            ['items', 'Items'],
            ...(showSources ? [['sources', 'Sources']] : []),
          ] as [CatalogView, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setViewChoice(key)}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              view === key
                ? 'border-tres-action text-tres-action'
                : 'border-transparent text-tres-text-secondary hover:text-tres-text-primary',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'sources' && showSources && <CatalogSourcesPanel />}
      {view !== 'sources' && (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(320px,1fr)_2fr]">
        {view === 'documents' ? (
          <DocumentsPanel
            docs={docs}
            selectedUri={selectedUri}
            onPick={handlePick}
            graphKey={CATALOG_GRAPH}
          />
        ) : (
          <ItemsPanel graph={CATALOG_GRAPH} selectedUri={selectedUri} onPick={handlePick} />
        )}

        {/* Detail panel — no graph selector (the page is cross-graph by design);
            the home-graph auto-shift lands edits on the entity's concrete graph. */}
        <div>
          {!selectedUri ? (
            <div className="rounded-lg border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
              {view === 'documents'
                ? 'Select a document or section on the left to view its details.'
                : 'Select a class on the left, then an item, to view its details.'}
            </div>
          ) : (
            <EditableEntity
              uri={selectedUri}
              onNavigate={handlePick}
              graphStorageKey="graphMultiPicker.catalog.detail"
              hideGraphSelector
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
}

// ===============================================================
// Documents view — :Document roots with their :contains hierarchy
// ===============================================================

function DocumentsPanel({
  docs,
  selectedUri,
  onPick,
  graphKey,
}: {
  docs: ReturnType<typeof useCatalogDocuments>;
  selectedUri: string | null;
  onPick: (uri: string) => void;
  graphKey: string;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Auto-expand the document roots on first successful load so the top-level
  // structure is visible without a click. Re-inits when the graph changes.
  const [initedFor, setInitedFor] = useState<string | null>(null);
  useEffect(() => {
    if (docs.data && initedFor !== graphKey) {
      setExpanded(new Set(docs.data.documents.map((d) => d.uri)));
      setInitedFor(graphKey);
    }
  }, [docs.data, initedFor, graphKey]);

  const allParentUris = useMemo(() => collectParents(docs.data?.documents ?? []), [docs.data]);

  const toggle = (uri: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uri)) next.delete(uri);
      else next.add(uri);
      return next;
    });

  const roots = docs.data?.documents ?? [];

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-sm font-medium text-tres-text-primary">
        <span>
          Documents
          {docs.data && (
            <span className="ml-2 text-xs font-normal text-tres-text-secondary">
              {roots.length}
            </span>
          )}
        </span>
        {roots.length > 0 && (
          <span className="flex gap-2 text-xs font-normal">
            <button
              type="button"
              onClick={() => setExpanded(new Set(allParentUris))}
              className={buttonVariants({ variant: 'link', size: 'md' })}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => setExpanded(new Set())}
              className={buttonVariants({ variant: 'link', size: 'md' })}
            >
              Collapse all
            </button>
          </span>
        )}
      </div>
      <div className="max-h-[70vh] overflow-y-auto p-2">
        {docs.isLoading && (
          <p className="px-2 py-1 text-sm text-tres-text-secondary">Loading documents…</p>
        )}
        {docs.error && (
          <p className="px-2 py-1 text-sm text-tres-error">{(docs.error as Error).message}</p>
        )}
        {docs.data && roots.length === 0 && (
          <p className="px-2 py-1 text-sm text-tres-text-secondary">
            {docs.data.graph_has_other_data
              ? 'No source documents in this graph — its contents browse under the Items view.'
              : 'No documents in this graph yet. Use the Database Management page to add document files, ' +
                'and their section structure will appear here.'}
          </p>
        )}
        <ul>
          {roots.map((node) => (
            <DocRow
              key={node.uri}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              selectedUri={selectedUri}
              onSelect={onPick}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

/** URIs of every node that has children — the set for Expand all. */
function collectParents(nodes: CatalogDocNode[]): string[] {
  const out: string[] = [];
  const walk = (n: CatalogDocNode) => {
    if (n.children && n.children.length > 0) {
      out.push(n.uri);
      n.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return out;
}

interface DocRowProps {
  node: CatalogDocNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (uri: string) => void;
  selectedUri: string | null;
  onSelect: (uri: string) => void;
}

function DocRow({ node, depth, expanded, onToggle, selectedUri, onSelect }: DocRowProps) {
  const hasChildren = !!node.children && node.children.length > 0;
  const isOpen = expanded.has(node.uri);
  const isSelected = selectedUri === node.uri;
  // Pre-listed indents so Tailwind doesn't purge them; the faithful document
  // model reaches L8, so nine steps (root + 8) are enough.
  const indents = [
    '',
    'pl-6',
    'pl-10',
    'pl-14',
    'pl-[4.5rem]',
    'pl-[5.5rem]',
    'pl-24',
    'pl-28',
    'pl-32',
  ];
  const indentClass = indents[Math.min(depth, indents.length - 1)] ?? 'pl-32';

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1 rounded py-1 pr-2 text-tres-text-primary hover:bg-muted',
          indentClass,
          isSelected && 'bg-tres-submenu-bg',
        )}
      >
        <button
          type="button"
          onClick={() => (hasChildren ? onToggle(node.uri) : undefined)}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex h-5 w-5 shrink-0 items-center justify-center')}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {hasChildren ? <Caret open={isOpen} /> : <span className="text-xs">·</span>}
        </button>
        <button
          type="button"
          onClick={() => onSelect(node.uri)}
          className={cn(
            'min-w-0 flex-1 truncate text-left hover:underline',
            node.level === 0 && 'font-medium',
          )}
          title={node.uri}
        >
          {node.label}
        </button>
        <CopyUriButton uri={node.uri} className="opacity-0 group-hover:opacity-100" />
        <span className="ml-1 shrink-0 rounded bg-muted px-1.5 text-xs text-tres-text-secondary">
          {node.level === 0 ? 'Doc' : `L${node.level}`}
        </span>
      </div>
      {isOpen && hasChildren && (
        <ul>
          {node.children!.map((child) => (
            <DocRow
              key={child.uri}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedUri={selectedUri}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      className={cn(
        'inline-block h-3 w-3 transform text-tres-text-secondary transition-transform',
        open ? 'rotate-90' : 'rotate-0',
      )}
      aria-hidden
    >
      ▶
    </span>
  );
}

// ===============================================================
// Items view — class list → paged instances (non-document catalogs)
// ===============================================================

function ItemsPanel({
  graph,
  selectedUri,
  onPick,
}: {
  graph: string | null;
  selectedUri: string | null;
  onPick: (uri: string) => void;
}) {
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [offset, setOffset] = useState(0);

  // Reset selection when the graph changes; reset paging on class/filter change.
  useEffect(() => {
    setSelectedClass(null);
    setSearch('');
  }, [graph]);
  useEffect(() => {
    setOffset(0);
  }, [selectedClass, debouncedSearch]);

  const classes = useCatalogClasses(graph);
  const instances = useCatalogInstances(graph, selectedClass, debouncedSearch, offset);

  const page = instances.data;
  const hasPrev = offset > 0;
  const hasNext = page ? page.offset + page.items.length < page.total : false;

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-sm font-medium text-tres-text-primary">
        Classes
        {classes.data && (
          <span className="ml-2 text-xs font-normal text-tres-text-secondary">
            {classes.data.classes.length}
          </span>
        )}
      </div>
      <div className="max-h-[30vh] overflow-y-auto p-2">
        {classes.isLoading && (
          <p className="px-2 py-1 text-sm text-tres-text-secondary">Loading classes…</p>
        )}
        {classes.error && (
          <p className="px-2 py-1 text-sm text-tres-error">{(classes.error as Error).message}</p>
        )}
        {classes.data && classes.data.classes.length === 0 && (
          <p className="px-2 py-1 text-sm text-tres-text-secondary">
            No typed entities in this graph yet. Load catalog data via the Database Management
            page.
          </p>
        )}
        {classes.data?.classes.map((c) => (
          <div
            key={c.uri}
            className={cn(
              'group flex w-full items-center rounded px-2 py-1 text-sm',
              selectedClass === c.uri
                ? 'bg-tres-submenu-bg font-medium text-tres-text-primary'
                : 'text-tres-text-primary hover:bg-muted',
            )}
          >
            <button
              type="button"
              onClick={() => setSelectedClass(c.uri)}
              className="min-w-0 flex-1 truncate text-left"
              title={c.uri}
            >
              {c.label}
            </button>
            <CopyUriButton uri={c.uri} className="opacity-0 group-hover:opacity-100" />
            <span className="ml-2 shrink-0 rounded bg-muted px-1.5 text-xs text-tres-text-secondary">
              {c.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>

      {selectedClass && (
        <>
          <div className="border-t border-border px-3 py-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by label…"
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="max-h-[40vh] overflow-y-auto p-2">
            {instances.isLoading && (
              <p className="px-2 py-1 text-sm text-tres-text-secondary">Loading items…</p>
            )}
            {instances.error && (
              <p className="px-2 py-1 text-sm text-tres-error">
                {(instances.error as Error).message}
              </p>
            )}
            {page?.items.map((item) => (
              <div
                key={item.uri}
                className={cn(
                  'group flex w-full items-center rounded px-2 py-1 text-sm',
                  selectedUri === item.uri
                    ? 'bg-tres-submenu-bg font-medium text-tres-text-primary'
                    : 'text-tres-text-primary hover:bg-muted',
                )}
              >
                <button
                  type="button"
                  onClick={() => onPick(item.uri)}
                  className="min-w-0 flex-1 truncate text-left"
                  title={item.uri}
                >
                  {item.label}
                </button>
                <CopyUriButton uri={item.uri} className="opacity-0 group-hover:opacity-100" />
              </div>
            ))}
            {page && page.items.length === 0 && (
              <p className="px-2 py-1 text-sm text-tres-text-secondary">No matches.</p>
            )}
          </div>
          {page && page.total > page.items.length && (
            <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-tres-text-secondary">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setOffset(Math.max(0, offset - page.limit))}
                className="rounded border border-input px-2 py-0.5 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                {page.offset + 1}–{page.offset + page.items.length} of{' '}
                {page.total.toLocaleString()}
              </span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() => setOffset(offset + page.limit)}
                className="rounded border border-input px-2 py-0.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
